/**
 * `<OneClickUnsubscribe />` — RFC 8058 compliance handler.
 *
 * Mounted by `pages/unsubscribe/+Page.tsx` when a `?t=<token>` query is
 * present. Fires `subscribe.unsubscribe` immediately on mount and
 * reuses the unsubscribed/error/invalid presentation from
 * `<UnsubscribeView />` (the prompt-step variant lives at
 * `/unsubscribe/<token>`).
 *
 * Per RFC 8058 the one-click flow MUST land in a stable terminal state
 * with no further user input — so there is no confirmation prompt here.
 */

import { useEffect, useState } from "react";

import type { SubscribeUnsubscribeOutput } from "@hantavirus/shared";

import { type TranslateFn } from "#/lib/i18n.js";
import { subscribeUnsubscribe } from "#/lib/subscribe-api.js";

type State =
  | { kind: "submitting" }
  | { kind: "result"; status: SubscribeUnsubscribeOutput["status"] }
  | { kind: "error" };

export type OneClickUnsubscribeProps = {
  token: string;
  locale: string;
  t: TranslateFn;
  apiBaseUrl?: string;
  /** Test-only override — replaces `subscribeUnsubscribe`. */
  unsubscribeFn?: (token: string) => Promise<SubscribeUnsubscribeOutput>;
};

export function OneClickUnsubscribe({
  token,
  locale,
  t,
  apiBaseUrl,
  unsubscribeFn,
}: OneClickUnsubscribeProps) {
  const [state, setState] = useState<State>({ kind: "submitting" });

  useEffect(() => {
    if (!token) {
      setState({ kind: "error" });
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const fn =
          unsubscribeFn ??
          ((tok: string) =>
            subscribeUnsubscribe(tok, { locale, apiBaseUrl }));
        const result = await fn(token);
        if (!cancelled) setState({ kind: "result", status: result.status });
      } catch {
        if (!cancelled) setState({ kind: "error" });
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, locale, apiBaseUrl, unsubscribeFn]);

  if (state.kind === "submitting") {
    return (
      <section
        aria-busy="true"
        className="flex flex-col items-center gap-4 py-12 text-center"
      >
        <p className="text-base text-[color:var(--color-fg-muted)]">
          {t("unsubscribe.submitting")}
        </p>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section
        role="alert"
        className="flex flex-col items-center gap-4 py-12 text-center"
      >
        <h1 className="text-2xl font-semibold text-[color:var(--color-fg-default)]">
          {t("unsubscribe.error.title")}
        </h1>
        <p className="text-base text-[color:var(--color-fg-muted)]">
          {t("unsubscribe.error.body")}
        </p>
      </section>
    );
  }

  if (state.status === "unsubscribed") {
    return (
      <section className="flex flex-col items-center gap-4 py-12 text-center">
        <span
          aria-hidden="true"
          className="text-4xl text-[color:var(--color-accent-resolved)]"
        >
          ✓
        </span>
        <h1 className="text-2xl font-semibold text-[color:var(--color-fg-default)]">
          {t("unsubscribe.done.title")}
        </h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          {t("unsubscribe.done.mistake")}{" "}
          <a
            href="/subscribe"
            className="underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-emergency)]"
          >
            {t("unsubscribe.done.resubscribe")}
          </a>
        </p>
      </section>
    );
  }

  return (
    <section
      role="alert"
      className="flex flex-col items-center gap-4 py-12 text-center"
    >
      <h1 className="text-2xl font-semibold text-[color:var(--color-fg-default)]">
        {t("unsubscribe.invalid.title")}
      </h1>
      <p className="text-base text-[color:var(--color-fg-muted)]">
        {t("unsubscribe.invalid.body")}
      </p>
    </section>
  );
}
